import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Building2, Search, Plus, Star, Phone, Mail, MapPin,
  Wrench, Shield, Car, Fuel, Eye, Truck, AlertTriangle, Scale,
  Loader2, RefreshCw, ChevronDown, X, Edit2, Trash2, CheckCircle, Upload, Sparkles
} from 'lucide-react';
import { supplierAPI, supplierContractAPI } from '../../api';
import SupplierDetailModal from './SupplierDetailModal';
import SupplierFormModal from './SupplierFormModal';
import VCardImportModal from './VCardImportModal';
import { ViewModeToggle } from '../common/ViewToggle';
import Pagination, { usePagination } from '../common/Pagination.jsx';

// A supplier seeded by onboarding (or the gold master) as a generic
// placeholder that the user is expected to rename/replace. Detected by the
// name starting with "Mon " (case-insensitive) so renaming to any real name
// silently removes the hint — no DB flag, no migration.
import PartnerLinkDot from './PartnerLinkDot.jsx';

const isPlaceholderSupplier = (s) => !!s && typeof s.name === 'string' && /^mon\s/i.test(s.name.trim());

const PLACEHOLDER_BANNER_DISMISS_KEY = 'fm_suppliers_placeholder_banner_dismissed';

// Category icons mapping
const CATEGORY_ICONS = {
  insurer: Shield,
  broker: Building2,
  garage: Wrench,
  tire: Car,
  fuel: Fuel,
  leasing: Car,
  glass: Car,
  bodywork: Car,
  towing: Truck,
  parking: MapPin,
  toll: Car,
  washing: Car,
  inspection: CheckCircle,
  equipment: Wrench,
  parts: Wrench,
  rental: Car,
  expert: Eye,
  legal: Scale,
  dealer: Building2,
  other: Building2,
};

// Category codes — labels are resolved at render via t('suppliers.categories.<code>')
// (module constants are evaluated once, before any language change; see CLAUDE.md i18n traps).
const CATEGORY_CODES = [
  'insurer', 'broker', 'garage', 'tire', 'fuel', 'leasing', 'glass', 'bodywork',
  'towing', 'parking', 'toll', 'washing', 'inspection', 'equipment', 'parts',
  'rental', 'expert', 'legal', 'dealer', 'other',
];

// Category colors
const CATEGORY_COLORS = {
  insurer: 'bg-blue-100 text-blue-800 border-blue-300',
  broker: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  garage: 'bg-orange-100 text-orange-800 border-orange-300',
  tire: 'bg-gray-100 text-gray-800 border-gray-300',
  fuel: 'bg-green-100 text-green-800 border-green-300',
  leasing: 'bg-purple-100 text-purple-800 border-purple-300',
  glass: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  bodywork: 'bg-red-100 text-red-800 border-red-300',
  towing: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  parking: 'bg-slate-100 text-slate-800 border-slate-300',
  toll: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  washing: 'bg-sky-100 text-sky-800 border-sky-300',
  inspection: 'bg-teal-100 text-teal-800 border-teal-300',
  equipment: 'bg-amber-100 text-amber-800 border-amber-300',
  parts: 'bg-stone-100 text-stone-800 border-stone-300',
  rental: 'bg-violet-100 text-violet-800 border-violet-300',
  expert: 'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300',
  legal: 'bg-rose-100 text-rose-800 border-rose-300',
  dealer: 'bg-lime-100 text-lime-800 border-lime-300',
  other: 'bg-neutral-100 text-neutral-800 border-neutral-300',
};

function FournisseursView({ onMessage }) {
  const { t } = useTranslation();
  const categoryLabels = useMemo(
    () => Object.fromEntries(CATEGORY_CODES.map((c) => [c, t(`suppliers.categories.${c}`)])),
    [t]
  );
  const [suppliers, setSuppliers] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // View mode: 'grouped' (default) or 'list'
  const [viewMode, setViewMode] = useState('grouped');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showPreferredOnly, setShowPreferredOnly] = useState(false);
  const [, setShowFilters] = useState(false); // Reserved for advanced filters UI

  // Expiring contracts alert — disabled until post-salon (supplier contracts feature not yet complete)
  const expiringContracts = [];
  // const [expiringContracts, setExpiringContracts] = useState([]);
  // useEffect(() => {
  //   supplierContractAPI.getExpiring(30).then(res => setExpiringContracts(res?.data || [])).catch(() => {});
  // }, []);

  // Modals
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showVCardImport, setShowVCardImport] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  // Placeholder banner (persistent dismissal via localStorage)
  const [placeholderBannerDismissed, setPlaceholderBannerDismissed] = useState(() => {
    try { return localStorage.getItem(PLACEHOLDER_BANNER_DISMISS_KEY) === '1'; }
    catch { return false; }
  });
  const dismissPlaceholderBanner = () => {
    try { localStorage.setItem(PLACEHOLDER_BANNER_DISMISS_KEY, '1'); } catch {}
    setPlaceholderBannerDismissed(true);
  };
  const placeholderCount = suppliers.filter(isPlaceholderSupplier).length;

  // Load suppliers
  const loadSuppliers = async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (selectedCategory) filters.category = selectedCategory;
      if (showPreferredOnly) filters.is_preferred = true;
      if (searchTerm) filters.search = searchTerm;

      const [suppliersRes, statsRes] = await Promise.all([
        supplierAPI.getAll(filters),
        supplierAPI.getStatsByCategory()
      ]);

      setSuppliers((suppliersRes?.data || []).filter(s => s.status !== 'deleted'));
      setCategoryStats(statsRes?.data || []);
    } catch (err) {
      console.error('[FournisseursView] Load error:', err);
      setError(err.message);
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: t('suppliers.messages.loadError'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuppliers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, showPreferredOnly]);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '') {
        loadSuppliers();
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Pagination
  const { currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, paginateArray } = usePagination(suppliers.length, 1, 25);

  // Handle supplier actions
  const handleViewDetails = async (supplier) => {
    try {
      const res = await supplierAPI.getById(supplier.id);
      setSelectedSupplier(res.data);
      setShowDetailModal(true);
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: t('suppliers.messages.detailsLoadError'),
        type: 'error'
      });
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setShowFormModal(true);
  };

  const handleCreate = () => {
    setEditingSupplier(null);
    setShowFormModal(true);
  };

  const handleDelete = async (supplier) => {
    if (!window.confirm(t('suppliers.messages.deleteConfirm', { name: supplier.name }))) return;

    try {
      await supplierAPI.delete(supplier.id);
      onMessage?.({
        title: t('common.messages.successTitle'),
        message: t('suppliers.messages.deleted'),
        type: 'success'
      });
      loadSuppliers();
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: t('suppliers.messages.deleteError'),
        type: 'error'
      });
    }
  };

  const handleFormSave = async (data) => {
    try {
      if (editingSupplier) {
        await supplierAPI.update(editingSupplier.id, data);
        onMessage?.({
          title: t('common.messages.successTitle'),
          message: t('suppliers.messages.updated'),
          type: 'success'
        });
      } else {
        await supplierAPI.create(data);
        onMessage?.({
          title: t('common.messages.successTitle'),
          message: t('suppliers.messages.created'),
          type: 'success'
        });
      }
      setShowFormModal(false);
      loadSuppliers();
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message || t('suppliers.messages.saveError'),
        type: 'error'
      });
    }
  };

  const handleTogglePreferred = async (supplier) => {
    try {
      await supplierAPI.update(supplier.id, { is_preferred: !supplier.is_preferred });
      loadSuppliers();
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: t('suppliers.messages.statusUpdateError'),
        type: 'error'
      });
    }
  };

  // Render category badge
  const CategoryBadge = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || Building2;
    const colorClass = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
    const label = categoryLabels[category] || category;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Expiring contracts alert */}
      {expiringContracts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="text-sm">
            <span className="font-semibold text-amber-800">{expiringContracts.length} contrat(s) fournisseur</span>
            <span className="text-amber-700"> expirant dans les 30 prochains jours</span>
            <span className="text-amber-600 ml-1">
              ({expiringContracts.slice(0, 3).map(c => c.supplier_name || c.contract_reference).join(', ')}{expiringContracts.length > 3 ? '...' : ''})
            </span>
          </div>
        </div>
      )}

      {/* Placeholder suppliers reminder — shown until explicitly dismissed */}
      {placeholderCount > 0 && !placeholderBannerDismissed && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm flex-1">
            <span className="font-semibold text-indigo-900">
              {t('suppliers.placeholder.bannerTitle', { count: placeholderCount })}
            </span>
            <span className="text-indigo-700">
              {' '}
              <Trans
                i18nKey="suppliers.placeholder.bannerText"
                components={{ icon: <Edit2 className="inline w-3 h-3 mx-0.5 -mt-0.5" /> }}
              />
            </span>
          </div>
          <button
            onClick={dismissPlaceholderBanner}
            className="text-indigo-500 hover:text-indigo-800 flex-shrink-0"
            title={t('suppliers.placeholder.dismiss')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-amber-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('suppliers.list.title')}</h1>
              <p className="text-sm text-gray-500">{t('suppliers.list.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Toggle - Reusable Component */}
            <ViewModeToggle
              value={viewMode}
              onChange={setViewMode}
              options={['grouped', 'list']}
              storageKey="fm_fournisseurs_viewMode"
            />
            <button
              onClick={loadSuppliers}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              title={t('suppliers.list.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowVCardImport(true)}
              className="flex items-center gap-2 px-4 py-2 border border-amber-400 text-amber-700 rounded-lg hover:bg-amber-50"
            >
              <Upload className="w-5 h-5" />
              {t('suppliers.list.importVCard')}
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 shadow-md"
            >
              <Plus className="w-5 h-5" />
              {t('suppliers.list.newSupplier')}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('suppliers.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Category Filter */}
          <div className="relative">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
            >
              <option value="">{t('suppliers.list.allCategories')}</option>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Preferred Only Toggle */}
          <button
            onClick={() => setShowPreferredOnly(!showPreferredOnly)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
              showPreferredOnly
                ? 'bg-yellow-100 border-yellow-400 text-yellow-800'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Star className={`w-4 h-4 ${showPreferredOnly ? 'fill-yellow-500' : ''}`} />
            {t('suppliers.list.preferredOnly')}
          </button>
        </div>
      </div>

      {/* Category Stats - Grouped View */}
      {viewMode === 'grouped' && categoryStats.length > 0 && !selectedCategory && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {categoryStats.map((stat) => {
            const Icon = CATEGORY_ICONS[stat.category] || Building2;
            const colorClass = CATEGORY_COLORS[stat.category] || CATEGORY_COLORS.other;
            return (
              <button
                key={stat.category}
                onClick={() => {
                  setSelectedCategory(stat.category);
                  setViewMode('list'); // Switch to list when clicking a category
                }}
                className="bg-white rounded-xl shadow-sm p-5 hover:shadow-lg transition text-left border border-gray-100 hover:border-amber-300"
              >
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-3 ${colorClass.replace('text-', 'bg-').replace('-800', '-100')}`}>
                  <Icon className={`w-6 h-6 ${colorClass.split(' ')[1]}`} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">
                  {categoryLabels[stat.category] || stat.category}
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-3xl font-bold text-gray-900">{stat.count}</span>
                  {stat.preferred_count > 0 && (
                    <span className="flex items-center gap-1 text-sm text-yellow-600 bg-yellow-50 px-2 py-1 rounded-full">
                      <Star className="w-4 h-4 fill-yellow-500" />
                      {stat.preferred_count}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Selected Category Header */}
      {selectedCategory && (
        <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <span className="text-amber-800 font-medium">
            {t('suppliers.list.categoryLabel', { category: categoryLabels[selectedCategory] })}
          </span>
          <button
            onClick={() => {
              setSelectedCategory('');
              setViewMode('grouped'); // Return to grouped view
            }}
            className="ml-auto flex items-center gap-1 text-amber-700 hover:text-amber-900 text-sm"
          >
            <X className="w-4 h-4" />
            {t('suppliers.list.viewAllCategories')}
          </button>
        </div>
      )}

      {/* Suppliers List - Show in list mode OR when a category is selected */}
      {(viewMode === 'list' || selectedCategory) && (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-red-500">
            <AlertTriangle className="w-12 h-12 mb-2" />
            <p>{error}</p>
            <button
              onClick={loadSuppliers}
              className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
            >
              {t('common.actions.retry')}
            </button>
          </div>
        ) : suppliers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Building2 className="w-12 h-12 mb-2 text-gray-300" />
            <p>{t('suppliers.list.noResults')}</p>
            {(searchTerm || selectedCategory || showPreferredOnly) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                  setShowPreferredOnly(false);
                }}
                className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              >
                {t('common.actions.resetFilters')}
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.supplier')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.category')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.contact')}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.city')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.rating')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.services')}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">{t('suppliers.list.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginateArray(suppliers).map((supplier) => (
                  <tr key={supplier.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleTogglePreferred(supplier)}
                          className={`p-1 rounded transition ${
                            supplier.is_preferred
                              ? 'text-yellow-500 hover:text-yellow-600'
                              : 'text-gray-300 hover:text-yellow-400'
                          }`}
                          title={supplier.is_preferred ? t('suppliers.actions.removeFavorite') : t('suppliers.actions.addFavorite')}
                        >
                          <Star className={`w-5 h-5 ${supplier.is_preferred ? 'fill-yellow-500' : ''}`} />
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">{supplier.name}</p>
                            <PartnerLinkDot supplier={supplier} />
                            {isPlaceholderSupplier(supplier) && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200"
                                title={t('suppliers.placeholder.tooltip')}
                              >
                                <Sparkles className="w-3 h-3" />
                                {t('suppliers.placeholder.badge')}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{supplier.code}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <CategoryBadge category={supplier.category} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="space-y-1">
                        {supplier.email && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Mail className="w-3 h-3" />
                            <span className="truncate max-w-32">{supplier.email}</span>
                          </div>
                        )}
                        {supplier.phone && (
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="w-3 h-3" />
                            {supplier.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {supplier.city && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="w-3 h-3" />
                          {supplier.city}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {supplier.rating ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                          <Star className="w-3 h-3 fill-green-600" />
                          {supplier.rating.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm text-gray-600">
                        {supplier.service_count || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleViewDetails(supplier)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title={t('suppliers.actions.viewDetails')}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(supplier)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                          title={t('common.actions.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(supplier)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                          title={t('common.actions.delete')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage={currentPage}
              totalItems={suppliers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              className="px-4 py-3 border-t bg-gray-50"
            />
          </div>
        )}
      </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedSupplier && (
        <SupplierDetailModal
          supplier={selectedSupplier}
          onClose={() => setShowDetailModal(false)}
          onEdit={() => {
            setShowDetailModal(false);
            handleEdit(selectedSupplier);
          }}
          categoryLabels={categoryLabels}
          categoryColors={CATEGORY_COLORS}
          categoryIcons={CATEGORY_ICONS}
        />
      )}

      {/* Form Modal */}
      {showFormModal && (
        <SupplierFormModal
          supplier={editingSupplier}
          onClose={() => setShowFormModal(false)}
          onSave={handleFormSave}
          categoryLabels={categoryLabels}
        />
      )}

      {/* vCard Import Modal */}
      {showVCardImport && (
        <VCardImportModal
          onClose={() => setShowVCardImport(false)}
          onSuccess={() => {
            onMessage?.({ title: t('suppliers.messages.importSuccessTitle'), message: t('suppliers.messages.importSuccessMessage'), type: 'success' });
            loadSuppliers();
          }}
          existingSuppliers={suppliers}
        />
      )}
    </div>
  );
}

export default FournisseursView;
